import axios, { AxiosError, AxiosInstance } from 'axios';

import { getToken } from '../utils/functions';

const httpClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutos em milissegundos
});

let isRefreshing = false;
type QueueItem = { resolve: (t: string) => void; reject: (e: unknown) => void };
let failedQueue: QueueItem[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) =>
    error ? p.reject(error) : p.resolve(token as string),
  );
  failedQueue = [];
};

httpClient.interceptors.request.use(
  async (config) => {
    if (typeof window !== 'undefined') {
      let token: string | null = null;

      try {
        token = await getToken();
      } catch (error) {
        token = null;
      }

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    const originalRequest = error.config || {};

    // backend usar 401 para credenciais erradas na tela de login
    if (
      typeof window !== 'undefined' &&
      status === 401 &&
      !localStorage.getItem('refresh_token')
    ) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      //window.location.href = '/';
      return Promise.reject(error);
    }

    const msg = error?.response?.data?.message;

    // backend usar 403 para token expirado
    if (
      status === 403 &&
      msg === 'Token Expired' &&
      !(originalRequest as any)._retry &&
      localStorage.getItem('refresh_token')
    ) {
      (originalRequest as any)._retry = true;

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          (originalRequest.headers as any).Authorization = `Bearer ${token}`;
          return httpClient(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const refresh_token = localStorage.getItem('refresh_token');
        const response = await axios.post(
          `${httpClient.defaults.baseURL}/api/auth/refresh`,
          { refresh_token },
        );

        const { access_token, refresh_token: newRefresh } = response.data;

        localStorage.setItem('token', access_token);
        localStorage.setItem('refresh_token', newRefresh);

        httpClient.defaults.headers.common.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        originalRequest.headers = originalRequest.headers || {};
        (originalRequest.headers as any).Authorization =
          `Bearer ${access_token}`;

        return httpClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.hash = '#/';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export const isHttpError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error);
export const isHttpCancel = (error: unknown): boolean => axios.isCancel(error);

export default httpClient;
